import { getLeyningOnDate } from '@hebcal/leyning';

/**
 * Hebrew book name mapping for Torah display.
 */
const BOOK_NAMES_HE = {
  'Genesis': 'בראשית',
  'Exodus': 'שמות',
  'Leviticus': 'ויקרא',
  'Numbers': 'במדבר',
  'Deuteronomy': 'דברים',
};

/**
 * Hebrew book name mapping for Haftarah / Nevi'im / Ketuvim.
 */
const NACH_NAMES_HE = {
  'Joshua': 'יהושע',
  'Judges': 'שופטים',
  'I Samuel': 'שמואל א׳',
  'II Samuel': 'שמואל ב׳',
  'I Kings': 'מלכים א׳',
  'II Kings': 'מלכים ב׳',
  'Isaiah': 'ישעיהו',
  'Jeremiah': 'ירמיהו',
  'Ezekiel': 'יחזקאל',
  'Hosea': 'הושע',
  'Joel': 'יואל',
  'Amos': 'עמוס',
  'Obadiah': 'עובדיה',
  'Jonah': 'יונה',
  'Micah': 'מיכה',
  'Nahum': 'נחום',
  'Habakkuk': 'חבקוק',
  'Zephaniah': 'צפניה',
  'Haggai': 'חגי',
  'Zechariah': 'זכריה',
  'Malachi': 'מלאכי',
};

/**
 * Translate a book code to Hebrew.
 * @param {string} book - English book name (e.g., "Genesis", "Isaiah")
 * @returns {string} Hebrew name, or the original if not found
 */
function bookToHebrew(book) {
  return BOOK_NAMES_HE[book] || NACH_NAMES_HE[book] || book;
}

/* ── Gematria (Hebrew numeral) conversion ─────────────────────────── */
const G_ONES = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
const G_TENS = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
const G_HUNDREDS = ['', 'ק', 'ר', 'ש', 'ת'];

/**
 * Convert a positive integer to Hebrew gematria letters.
 * @param {number} n
 * @returns {string}
 */
function toGematria(n) {
  if (!n || n <= 0) return String(n);
  let result = '';
  let remaining = n;
  while (remaining >= 400) { result += 'ת'; remaining -= 400; }
  if (remaining >= 100) { result += G_HUNDREDS[Math.floor(remaining / 100)]; remaining %= 100; }
  if (remaining === 15) return result + 'טו';
  if (remaining === 16) return result + 'טז';
  if (remaining >= 10) { result += G_TENS[Math.floor(remaining / 10)]; remaining %= 10; }
  result += G_ONES[remaining];
  return result;
}

/**
 * Convert all numbers in a reference string to Hebrew gematria.
 * "1:1" → "א:א", "42:5-43:11" → "מב:ה-מג:יא"
 * @param {string} ref
 * @returns {string}
 */
function refToHebrew(ref) {
  if (!ref) return ref;
  return ref.replace(/\d+/g, (m) => toGematria(parseInt(m, 10)));
}

/**
 * Translate a haftarah citation string to Hebrew.
 * Handles formats like "Isaiah 42:5-43:11" or "I Kings 7:40-50"
 * @param {string} citation
 * @returns {string} Hebrew citation
 */
function haftaraToHebrew(citation) {
  if (!citation) return null;
  // Replace English book name with Hebrew
  let result = citation.replace(/^([A-Z][A-Za-z ]+?)(?=\s\d)/gm, (match) => {
    return bookToHebrew(match.trim());
  });
  // Convert all Arabic numerals to Hebrew gematria
  result = refToHebrew(result);
  return result;
}

/**
 * Get Torah reading data for a given Hebrew date.
 *
 * @param {import('@hebcal/core').HDate} hdate
 * @param {boolean} isIsrael
 * @returns {object|null} leyning data
 */
export function getLeyningData(hdate, isIsrael) {
  const ley = getLeyningOnDate(hdate, isIsrael);
  if (!ley) return null;

  const aliyot = [];
  const fk = ley.fullkriyah || {};
  for (const key of Object.keys(fk)) {
    if (key === 'M') continue; // maftir handled separately
    const a = fk[key];
    aliyot.push({
      num: parseInt(key, 10),
      book: a.k,
      bookHe: bookToHebrew(a.k),
      begin: a.b,
      end: a.e,
      beginHe: refToHebrew(a.b),
      endHe: refToHebrew(a.e),
    });
  }

  const maftir = fk.M ? {
    num: 'M',
    book: fk.M.k,
    bookHe: bookToHebrew(fk.M.k),
    begin: fk.M.b,
    end: fk.M.e,
    beginHe: refToHebrew(fk.M.b),
    endHe: refToHebrew(fk.M.e),
  } : null;

  const haftaraEn = ley.haftara || null;
  const haftaraHe = haftaraToHebrew(haftaraEn);

  return {
    torah: aliyot,
    haftara: haftaraEn,
    haftaraHe,
    maftir,
    reason: ley.reason?.haftara || null,
    summary: ley.summary || null,
  };
}
